/** UI state (not synced). View preferences persist in localStorage per device. */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { GroupSort, ISODate, MyTasksGroupKey } from '@karya/core';

export interface QuickAddPrefill {
  tab?: 'task' | 'reminder';
  listId?: string | null;
  dueDate?: ISODate | null;
  dueTime?: string | null;
  parentTaskId?: string | null;
  statusId?: string | null;
  text?: string;
}

export interface ListViewPrefs {
  groupBy: 'status' | 'priority' | 'due' | 'none';
  subtasks: 'collapsed' | 'expanded' | 'separate';
  showClosed: boolean;
  showEmptyGroups: boolean;
  columns: { due: boolean; priority: boolean; tags: boolean; start: boolean; created: boolean };
  sort: {
    key: 'manual' | 'due_date' | 'priority' | 'created' | 'updated' | 'name';
    direction: 'asc' | 'desc';
  };
  filters: {
    statusTypes: string[];
    priorities: number[];
    tagIds: string[];
    due: 'any' | 'overdue' | 'today' | 'week' | 'none';
  };
}

export const DEFAULT_LIST_PREFS: ListViewPrefs = {
  groupBy: 'status',
  subtasks: 'collapsed',
  showClosed: false,
  showEmptyGroups: false,
  columns: { due: true, priority: true, tags: true, start: false, created: false },
  sort: { key: 'manual', direction: 'asc' },
  filters: { statusTypes: [], priorities: [], tagIds: [], due: 'any' },
};

interface UIState {
  quickAdd: QuickAddPrefill | null;
  openQuickAdd: (p?: QuickAddPrefill) => void;
  closeQuickAdd: () => void;
  paletteOpen: boolean;
  setPaletteOpen: (v: boolean) => void;
  shortcutsOpen: boolean;
  setShortcutsOpen: (v: boolean) => void;
  focusedTaskId: string | null;
  setFocusedTaskId: (id: string | null) => void;
  selection: string[];
  setSelection: (ids: string[]) => void;
}

export const useUI = create<UIState>((set) => ({
  quickAdd: null,
  openQuickAdd: (p = {}) => set({ quickAdd: p }),
  closeQuickAdd: () => set({ quickAdd: null }),
  paletteOpen: false,
  setPaletteOpen: (v) => set({ paletteOpen: v }),
  shortcutsOpen: false,
  setShortcutsOpen: (v) => set({ shortcutsOpen: v }),
  focusedTaskId: null,
  setFocusedTaskId: (id) => set({ focusedTaskId: id }),
  selection: [],
  setSelection: (ids) => set({ selection: ids }),
}));

interface PrefsState {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (v: boolean) => void;
  expandedSpaces: Record<string, boolean>;
  toggleSpace: (id: string) => void;
  expandedFolders: Record<string, boolean>;
  toggleFolder: (id: string) => void;
  favorites: string[];
  toggleFavorite: (listId: string) => void;
  myTasksCollapsed: Partial<Record<MyTasksGroupKey, boolean>>;
  toggleMyTasksGroup: (g: MyTasksGroupKey) => void;
  myTasksSort: Partial<Record<MyTasksGroupKey, GroupSort>>;
  setMyTasksSort: (g: MyTasksGroupKey, s: GroupSort) => void;
  listPrefs: Record<string, ListViewPrefs>;
  setListPrefs: (listId: string, p: Partial<ListViewPrefs>) => void;
  expandedTasks: Record<string, boolean>;
  toggleTaskExpanded: (id: string) => void;
  recents: { kind: 'task' | 'list'; id: string; at: number }[];
  pushRecent: (kind: 'task' | 'list', id: string) => void;
  calendarMode: 'month' | 'week';
  setCalendarMode: (m: 'month' | 'week') => void;
}

export const usePrefs = create<PrefsState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      setSidebarCollapsed: (v) => set({ sidebarCollapsed: v }),
      expandedSpaces: {},
      toggleSpace: (id) =>
        set((s) => ({
          expandedSpaces: { ...s.expandedSpaces, [id]: !(s.expandedSpaces[id] ?? true) },
        })),
      expandedFolders: {},
      toggleFolder: (id) =>
        set((s) => ({
          expandedFolders: { ...s.expandedFolders, [id]: !(s.expandedFolders[id] ?? true) },
        })),
      favorites: [],
      toggleFavorite: (listId) =>
        set((s) => ({
          favorites: s.favorites.includes(listId)
            ? s.favorites.filter((x) => x !== listId)
            : [...s.favorites, listId],
        })),
      myTasksCollapsed: { next: false, later: true, unscheduled: true },
      toggleMyTasksGroup: (g) =>
        set((s) => ({ myTasksCollapsed: { ...s.myTasksCollapsed, [g]: !s.myTasksCollapsed[g] } })),
      myTasksSort: {},
      setMyTasksSort: (g, sort) => set((s) => ({ myTasksSort: { ...s.myTasksSort, [g]: sort } })),
      listPrefs: {},
      setListPrefs: (listId, p) =>
        set((s) => ({
          listPrefs: {
            ...s.listPrefs,
            [listId]: { ...DEFAULT_LIST_PREFS, ...s.listPrefs[listId], ...p },
          },
        })),
      expandedTasks: {},
      toggleTaskExpanded: (id) =>
        set((s) => ({ expandedTasks: { ...s.expandedTasks, [id]: !s.expandedTasks[id] } })),
      recents: [],
      pushRecent: (kind, id) =>
        set((s) => ({
          recents: [{ kind, id, at: Date.now() }, ...s.recents.filter((r) => r.id !== id)].slice(
            0,
            20,
          ),
        })),
      calendarMode: 'month',
      setCalendarMode: (m) => set({ calendarMode: m }),
    }),
    { name: 'karya-prefs', version: 1 },
  ),
);

export function useListPrefs(listId: string): ListViewPrefs {
  return usePrefs((s) => s.listPrefs[listId]) ?? DEFAULT_LIST_PREFS;
}
